import {
  Alert as ChakraAlert,
  AlertDescription,
  AlertIcon as ChakraAlertIcon,
  AlertIconProps,
  AlertProps as ChakraAlertProps,
  AlertTitle,
  Box,
} from '@chakra-ui/react';
import { UnreachableCaseError } from '@errors/unreachable';
import { WarningIcon } from '@ui/components/Icons';

export const AlertStatuses = [
  'info',
  'warning',
  'success',
  'error',
  'loading',
] as const satisfies NonNullable<ChakraAlertProps['status'][]>;

export type AlertStatus = (typeof AlertStatuses)[number];

export type AlertProps = {
  title?: string;
  description?: string;
  status: AlertStatus;
  children?: React.ReactNode;
};

export function Alert({ title, description, status, children }: AlertProps) {
  return (
    <ChakraAlert status={status} flexShrink={0}>
      <AlertIcon status={status} />
      {title && <AlertTitle>{title}</AlertTitle>}
      {description && <AlertDescription>{description}</AlertDescription>}
      {children}
    </ChakraAlert>
  );
}

export function BoxAlert({ title, description, status, children }: AlertProps) {
  return (
    <ChakraAlert status={status} flexShrink={0}>
      <AlertIcon status={status} />
      <Box>
        <AlertTitle>{title}</AlertTitle>
        {description && <AlertDescription>{description}</AlertDescription>}
        {children}
      </Box>
    </ChakraAlert>
  );
}

export function AlertIcon({
  status,
  ...props
}: { status: AlertStatus } & AlertIconProps) {
  switch (status) {
    case 'info':
      return <ChakraAlertIcon color="blue.500" {...props} />;
    case 'warning':
      return <ChakraAlertIcon as={WarningIcon} color="yellow.500" {...props} />;
    case 'success':
      return <ChakraAlertIcon color="green.500" {...props} />;
    case 'error':
      return <ChakraAlertIcon color="snap.red.600" {...props} />;
    case 'loading':
      return <ChakraAlertIcon {...props} />;
    default:
      throw new UnreachableCaseError(status);
  }
}
